import Bluebird from 'bluebird';
import * as $ from 'jquery';
import CacheG from '../../../../../services/cache';
import { addEvent } from '../../dom';
import InputField from '../input-field';
import AutocompleteResults from './autocomplete-results';

// TODO: Split up file
// tslint:disable:max-file-line-count

export default class AutocompleteField<T> extends InputField {

    public readonly inputTimeout: number = 300;
    private readonly _searchCache = new CacheG<T[]>();
    private readonly _changeSubscribers: Array<(selected: T | null, prevVal: T | null) => void> = [];
    private readonly _selectSubscribers: Array<(selected: T | null) => void> = [];
    private readonly _resultsList: AutocompleteResults;

    private _inputTimer: number | undefined;
    private _searchResults: T[] | null = null;
    private _prevVal: T | null = null;
    private _activeResultIndex = -1; // Selected by keyboard
    private _currentInput = '';

    private getSearchResults: (input: string) => Bluebird<T[]>;
    private formatValue: (value: T) => string;
    private formatResult: (value: T) => string;
    private getSeperator: (before: T | null, after: T | null) => Bluebird<string | undefined>;
    private getNoResultsMsg: string | (() => Bluebird<string>);

    private readonly _maxResults = Infinity;
    private readonly _resultsClass: string = 'ac-result';

    private _searching: Bluebird<void> = Bluebird.resolve();
    public get done() {
        return this._searching;
    }

    private _selectedResult: T | null = null;
    public get selectedResult() { return this._selectedResult; }

    constructor(id: string, data: {
        // Provide search results
        search: (input: string) => Bluebird<T[]>,

        // Value to show in input field when selected
        formatValue?: (value: T) => string,

        // HTML row to show in result list
        formatResult?: (value: T) => string,

        // HTML seperator between results
        addSeperator?: (before: T | null, after: T | null) => Bluebird<string | undefined>,

        // Optionally show this message when there are no results
        noResultMessage?: string | (() => Bluebird<string>)

    }, options?: {
        // Disable input timeout to decrease search requests
        noDelay?: boolean,

        // Maximum results shown at once, default is Infinity
        maxResults?: number,

        // Provide a class name to add to the results container
        resultsClass?: string,

        // Automatically select the first result if none is selected
        autoSelect?: boolean
    }) {
        super(id);

        // Data functions
        this.getSearchResults = data.search;
        // @ts-ignore
        this.formatValue = data.formatValue || (x => x.toString());
        this.formatResult = data.formatResult || this.formatValue;
        this.getSeperator = data.addSeperator || (() => Bluebird.resolve(''));
        this.getNoResultsMsg = data.noResultMessage || '';

        // AC options
        if (options) {
            if (options.noDelay) this.inputTimeout = 0;
            if (options.maxResults) this._maxResults = options.maxResults;
            if (options.resultsClass) this._resultsClass = options.resultsClass;
        }

        this.$JQuery
            .on('input', () => this.handleInput(this.value))
            .prop('autocomplete', 'off')
            .focusin(() => this._resultsList.show())
            .focusout(() => {
                if (this._selectedResult) {
                    this._resultsList.hide();
                    return;
                }

                // No result selected yet
                if (this._searchResults && this._activeResultIndex >= 0) {
                    // Results loaded and one active
                    const activeResult = this._searchResults[this._activeResultIndex];
                    this.setValue(activeResult);
                    this.triggerSelectEvent(activeResult);
                    this._resultsList.hide();
                } else if (options && options.autoSelect) {
                    // Automatically select first result
                    this.done.then(() => this.autoSelectResult());
                } else {
                    // Do nothing
                    this._resultsList.hide();
                }
            });

        // Inject results list div
        const $resultList = $('<div class="ac-results">');
        this._resultsList = new AutocompleteResults($resultList, this._resultsClass);
        this.$JQuery.after($resultList);

        this._resultsList.onSelect(rID => {
            if (!this._searchResults) return;

            const selected = this._searchResults[rID] || null;
            if (!selected) throw new Error(`Result '${rID}' does not exist`);

            this.setValue(selected);
            this.setActiveResult(rID);
            this.triggerSelectEvent(selected);
            this._resultsList.hide();
        });

        this.loadArrowKeysHandler();
    }

    public onChangeAC(handler: (selected: T | null,
        prevVal: T | null) => void): void {
        this._changeSubscribers.push(handler);
    }

    public onSelect(handler: (selected: T | null) => void): void {
        this._selectSubscribers.push(handler);
    }

    public setValue(value: T): void {
        this._selectedResult = value;
        this.value = this.formatValue(value);
        this.triggerChangeEvent();
    }

    public async clear(): Bluebird<void> {
        this._selectedResult = null;
        this.value = '';
        await this._resultsList.hide();
        await this.setResults([]);
        this.triggerChangeEvent();
    }

    private async autoSelectResult(): Bluebird<void> {
        // Selects first result if none is selected
        await this.search();

        const resultActive =
            this._activeResultIndex !== -1
            && this.selectedResult !== null;

        if (!resultActive
            && this._searchResults
            && this._searchResults.length > 0) {
            this.setValue(this._searchResults[0]);
        }

        return this._resultsList.hide();
    }

    public async searchValue(value: string): Bluebird<void> {
        // Searches and selects result without showing the list
        this.val(value);
        await this.search();
        return this.autoSelectResult();
    }

    private handleInput(input: string): void {
        clearTimeout(this._inputTimer);
        this._selectedResult = null;

        if (!input) {
            this.clear();
            return;
        }

        this._resultsList.show();

        // Check if results already cached
        const cachedResults = this._searchCache.find(input);
        if (cachedResults) {
            this.setResults(cachedResults);
            return;
        }

        // Else wait for delay
        this._inputTimer = window.setTimeout(() => {
            this.search();
        }, this.inputTimeout);
    }

    private search(): Bluebird<void> {
        const input = this.value;
        if (!input || !input.trim()) return Bluebird.resolve();

        this._currentInput = input;
        const cachedResults = this._searchCache.find(input);

        const search = cachedResults
            ? Bluebird.resolve(cachedResults)
            : this.getSearchResults(input);

        this._searching = search.then(results => {
            this._searchCache.set(input, results);
            return this.setResults(results);
        });

        return this._searching;
    }

    private async setResults(results: T[]): Bluebird<void> {
        this._searchResults = results;
        this._activeResultIndex = -1;

        // Add results to view
        results = results.slice(0, this._maxResults);

        const divs: string[] = [];
        let prevVal: T | null = null;
        for (let i = 0; i < results.length; i++) {
            const result = results[i];
            const seperator = await this.getSeperator(prevVal, result);
            if (seperator) divs.push(seperator);
            prevVal = result;

            const divClass = this._resultsClass ? ` class='${this._resultsClass}'` : '';
            divs.push(`<div data-id=${i}${divClass}>${this.formatResult(result)}</div>`);
        }

        const noResultsMsg = typeof this.getNoResultsMsg === 'function'
            ? await this.getNoResultsMsg()
            : this.getNoResultsMsg;

        if (divs.length === 0 && noResultsMsg) {
            divs.push(`<div class='no-results-msg'>${noResultsMsg}</div>`);
        }

        return this._resultsList.setResults(divs);
    }

    private triggerChangeEvent(): void {
        if (this.selectedResult !== this._prevVal) {
            this._changeSubscribers.forEach(x => x(this.selectedResult, this._prevVal));
            this._prevVal = this.selectedResult;
        }
    }

    private triggerSelectEvent(val: T | null): void {
        this._selectSubscribers.forEach(x => x(val));
    }

    private setActiveResult(index: number): void {
        this._activeResultIndex = index;
        if (index < 0) {
            this.val(this._currentInput);
            this._resultsList.setActiveResult(null);
        } else if (this._searchResults && index < this._searchResults.length) {
            this.value = this.formatValue(this._searchResults[index]);
            this._resultsList.setActiveResult(index);
        }
    }

    private loadArrowKeysHandler(): void {
        addEvent('keydown', this.el, e => {
            if (!this._searchResults) return;

            let triggered = true;
            switch ((e as KeyboardEvent).key) {
                case 'ArrowDown':
                    if (this._activeResultIndex < this._searchResults.length - 1) {
                        this._activeResultIndex++;
                    }
                    break;
                case 'ArrowUp':
                    if (this._activeResultIndex >= 0) {
                        this._activeResultIndex--;
                    }
                    break;
                default:
                    triggered = false;
                    break;
            }

            if (triggered) {
                this._selectedResult = null;
                this.setActiveResult(this._activeResultIndex);
                e.preventDefault();
            }
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/services/input-fields/autocomplete/autocomplete-field.ts